// src/services/contentServiceNew.js
import { api } from './api';
import { objectToQueryString } from '../utils/apiUtils';
import {
  createService,
  createFileUploadConfig,
  createNestedCollection
} from './factory';

// Create the base content service using the factory
const contentServiceBase = createService('Content', '/content', {
  fileUploadOptions: createFileUploadConfig('file', 'assets'),
  nestedCollections: [
    createNestedCollection('assets', 'asset'),
    createNestedCollection('comments', 'comment'),
    createNestedCollection('references', 'reference'),
    createNestedCollection('versions', 'version'),
    createNestedCollection('analytics')
  ],
  customEndpoints: {
    // Get content calendar
    getContentCalendar: async (params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`/content/calendar${queryString}`);
    },
    
    // Initialize approval workflow
    initApprovalWorkflow: async (contentId, workflowData) => {
      return api.post(`/content/${contentId}/workflow`, workflowData);
    },
    
    // Approve content
    approveContent: async (contentId, approvalData) => {
      return api.post(`/content/${contentId}/approve`, approvalData);
    },
    
    // Reject content
    rejectContent: async (contentId, rejectionData) => {
      return api.post(`/content/${contentId}/reject`, rejectionData);
    },
    
    // Schedule content
    scheduleContent: async (contentId, scheduleData) => {
      return api.post(`/content/${contentId}/schedule`, scheduleData);
    },
    
    // Publish content
    publishContent: async (contentId, publishData = {}) => {
      return api.post(`/content/${contentId}/publish`, publishData);
    },
    
    // Revert to previous version
    revertToVersion: async (contentId, versionNumber) => {
      return api.post(`/content/${contentId}/revert`, { versionNumber });
    },
    
    // Search content
    searchContent: async (query, params = {}) => {
      const searchParams = { ...params, query };
      const queryString = objectToQueryString(searchParams);
      return api.get(`/content/search${queryString}`);
    },
    
    // Get content for a parent entity (brand, campaign)
    getFromParent: async (parentId, parentType, params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`/${parentType}s/${parentId}/content${queryString}`);
    }
  }
});

// Create backwards-compatible exports that use the factory methods underneath
export const getContent = async (params = {}) => {
  return contentServiceBase.getAll(params);
};

export const getContentById = async (contentId) => {
  return contentServiceBase.getById(contentId);
};

export const createContent = async (contentData) => {
  return contentServiceBase.create(contentData);
};

export const updateContent = async (contentId, contentData) => {
  return contentServiceBase.update(contentId, contentData);
};

export const deleteContent = async (contentId) => {
  return contentServiceBase.delete(contentId);
};

export const getContentCalendar = async (params = {}) => {
  return contentServiceBase.getContentCalendar(params);
};

export const initApprovalWorkflow = async (contentId, workflowData) => {
  return contentServiceBase.initApprovalWorkflow(contentId, workflowData);
};

export const approveContent = async (contentId, approvalData) => {
  return contentServiceBase.approveContent(contentId, approvalData);
};

export const rejectContent = async (contentId, rejectionData) => {
  return contentServiceBase.rejectContent(contentId, rejectionData);
};

export const scheduleContent = async (contentId, scheduleData) => {
  return contentServiceBase.scheduleContent(contentId, scheduleData);
};

export const publishContent = async (contentId, publishData = {}) => {
  return contentServiceBase.publishContent(contentId, publishData);
};

export const getContentAnalytics = async (contentId, params = {}) => {
  return contentServiceBase.getAnalytics(contentId, params);
};

export const getContentComments = async (contentId) => {
  return contentServiceBase.getComments(contentId);
};

export const addContentComment = async (contentId, commentData) => {
  return contentServiceBase.addComment(contentId, commentData);
};

export const getBrandContent = async (brandId, params = {}) => {
  return contentServiceBase.getFromParent(brandId, 'brand', params);
};

export const getCampaignContent = async (campaignId, params = {}) => {
  return contentServiceBase.getFromParent(campaignId, 'campaign', params);
};

export const getContentVersions = async (contentId) => {
  return contentServiceBase.getVersions(contentId);
};

export const revertToVersion = async (contentId, versionNumber) => {
  return contentServiceBase.revertToVersion(contentId, versionNumber);
};

export const searchContent = async (query, params = {}) => {
  return contentServiceBase.searchContent(query, params);
};

// Custom file upload methods
export const uploadContentAsset = async (contentId, file, metadata = {}) => {
  try {
    const formData = new FormData();
    
    // Append the file with the field name 'file'
    formData.append('file', file);
    
    // Append metadata fields
    if (metadata.type) formData.append('type', metadata.type);
    if (metadata.caption) formData.append('caption', metadata.caption);
    if (metadata.altText) formData.append('altText', metadata.altText);
    
    // Any other metadata fields
    Object.entries(metadata).forEach(([key, value]) => {
      if (!['type', 'caption', 'altText'].includes(key) && value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    
    const response = await api.postFiles(`/content/${contentId}/assets`, formData);
    
    return response.data.asset || response;
  } catch (error) {
    console.error('Error uploading content asset:', error);
    throw error;
  }
};

export const uploadMultipleContentAssets = async (contentId, assets) => {
  try {
    const uploadPromises = assets.map(asset => {
      if (!asset.file) {
        throw new Error('Asset file is required');
      }
      
      return uploadContentAsset(
        contentId,
        asset.file,
        {
          type: asset.type || 'image',
          caption: asset.caption || asset.file.name,
          altText: asset.altText || '',
          ...asset.metadata
        }
      );
    });
    
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading multiple content assets:', error);
    throw error;
  }
};

export const addContentReference = async (contentId, reference) => {
  try {
    let response;
    
    if (reference instanceof FormData) {
      response = await api.postFiles(`/content/${contentId}/references`, reference);
    } else {
      response = await api.post(`/content/${contentId}/references`, reference);
    }
    
    return response.data.reference || response;
  } catch (error) {
    console.error('Error adding content reference:', error);
    throw error;
  }
};

export const uploadContentReferences = async (contentId, references) => {
  try {
    const uploadPromises = references.map(reference => {
      // If reference has a file to upload
      if (reference.file) {
        const formData = new FormData();
        formData.append('file', reference.file);
        formData.append('type', reference.type || 'image');
        formData.append('caption', reference.caption || reference.file.name);
        formData.append('altText', reference.altText || '');
        
        return addContentReference(contentId, formData);
      } 
      // If reference is just a URL or already uploaded
      else if (reference.url) {
        return addContentReference(contentId, reference);
      }
    });
    
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading content references:', error);
    throw error;
  }
};

export const removeContentReference = async (contentId, referenceId) => {
  return contentServiceBase.removeReference(contentId, referenceId);
};

// Create a full service object that includes all methods
const contentService = {
  getContent,
  getContentById,
  createContent,
  updateContent,
  deleteContent,
  uploadContentAsset,
  uploadMultipleContentAssets,
  addContentReference,
  uploadContentReferences,
  removeContentReference,
  getContentCalendar,
  initApprovalWorkflow,
  approveContent,
  rejectContent,
  scheduleContent,
  publishContent,
  getContentAnalytics,
  getContentComments,
  addContentComment,
  getBrandContent,
  getCampaignContent,
  getContentVersions,
  revertToVersion,
  searchContent
};

export default contentService;